import {useEffect,useMemo,useRef,useState} from 'react';
import type {SmmConnectionState,SmmOrderProjection,SmmReadModelSnapshot,SmmOrderPageState} from './product-types';
import type {MobileOrderQuery,MobileOrderPage} from '../../../contracts/mobile-order-pages-v1';
import {operationalOrderSearchPage} from './order-pages';
import {StageXState,type SmmStageXKind} from './StageXState';
import {
  smmStage7AmountLabel,
  smmStage7ConnectionState,
  smmStage7InSegment,
  smmStage7ItemCount,
  smmStage7MatchesDate,
  smmStage7MatchesSearch,
  smmStage7MatchesSource,
  smmStage7MatchesStatus,
  smmStage7OrderTime,
  smmStage7Phone,
  smmStage7SourceGroup,
  smmStage7StatusLabel,
  smmStage7Sort,
  type SmmStage7DateFilter,
  type SmmStage7SearchScope,
  type SmmStage7Segment,
  type SmmStage7SourceFilter,
  type SmmStage7StatusFilter,
} from './stage7-orders.mjs';

type Stage7Surface='LIST'|'SEARCH'|'DETAIL'|'STATUS';
type ServiceFilter='ALL'|'TAKEAWAY'|'DINE_IN'|'ATTENTION';
const matchesService=(row:SmmOrderProjection,filter:ServiceFilter)=>filter==='ALL'||filter==='ATTENTION'&&(row.readback!=='CONFIRMED'||smmStage7StatusLabel(row)==='狀態未明')||row.serviceMode===filter;

function Stage7Paging({page,busy,onPage}:{page:SmmOrderPageState|undefined;busy:boolean;onPage?:((query:MobileOrderQuery)=>Promise<void>)}){
  if(!page)return null;
  return <section aria-label="目前訂單分頁">
    <p className="stage7-summary">已載入 {page.loadedCount} 張；{page.hasMore?'尚有記錄可載入。':'只顯示已讀回資料。'}</p>
    {page.adminCoverage==='PARTIAL'?<p className="stage7-summary">部分資料未能分類或缺少營業日，請在收銀機核對。</p>:null}
    {page.hasMore?<button type="button" className="stage7-refresh-button" disabled={busy||!onPage} onClick={()=>void onPage?.({...page.query,cursor:page.nextCursor!})}>{busy?'載入中…':page.query.view==='HISTORY'?'載入更多歷史訂單':'載入更多未完成訂單'}</button>:null}
  </section>;
}

const SOURCE_FILTERS:readonly [SmmStage7SourceFilter,string][]=[
  ['ALL','全部'],
  ['ONSITE','現場'],
  ['SMM','手機點單'],
  ['OWN_PLATFORM','自家平台'],
  ['THIRD_PARTY','第三方'],
];

type SearchResult=ReturnType<typeof operationalOrderSearchPage>;
type ServerSearch={result:SearchResult|null;busy:boolean;disabled:boolean;error:string;onSubmit:()=>void;onMore:()=>void};

const SEARCH_SCOPES:readonly [SmmStage7SearchScope,string][]=[
  ['ALL','全部'],
  ['DISPLAY','訂單編號'],
  ['PRODUCT','商品名稱'],
  ['PHONE','電話'],
];


const ACTIVE_STATUS_FILTERS:readonly [SmmStage7StatusFilter,string][]=[
  ['ALL','全部狀態'],
  ['PENDING','待確認'],
  ['WORKING','製作中'],
  ['READY','可取餐'],
  ['UNKNOWN','狀態未明'],
];
const HISTORY_STATUS_FILTERS:readonly [SmmStage7StatusFilter,string][]=[
  ['ALL','全部狀態'],
  ['PENDING','待確認'],
  ['WORKING','製作中'],
  ['READY','可取餐'],
  ['PICKED_UP','已取餐'],
  ['CANCELLED','已取消'],
  ['UNKNOWN','狀態未明'],
];

function dateTimeLabel(value:string){
  const date=new Date(value);
  if(!Number.isFinite(date.getTime()))return '未有資料';
  return date.toLocaleString('zh-HK',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',hour12:false});
}

function timeLabel(value:string){
  const date=new Date(value);
  if(!Number.isFinite(date.getTime()))return '未有資料';
  return date.toLocaleTimeString('zh-HK',{hour:'2-digit',minute:'2-digit',hour12:false});
}

function statusTone(label:string){
  if(label==='已取餐')return 'success';
  if(label==='已取消')return 'cancelled';
  if(label.includes('可取餐')||label.includes('完成'))return 'ready';
  if(label==='製作中')return 'working';
  if(label==='待確認')return 'pending';
  if(label==='狀態未明'||label==='部分資料')return 'unknown';
  return 'neutral';
}

function sourceLabel(row:SmmOrderProjection){
  const raw=String(row.source||'').trim();
  const group=smmStage7SourceGroup(row);
  return group==='OWN_PLATFORM'?'網店':group==='SMM'?'手機點單':group==='ONSITE'?'現場':raw||'未有資料';
}

function sourceFilterCount(rows:readonly SmmOrderProjection[],filter:SmmStage7SourceFilter){
  return rows.filter(row=>smmStage7MatchesSource(row,filter)).length;
}

function Stage7StateBanner({connection,hasRows}:{connection:SmmConnectionState;hasRows:boolean}){
  const state=smmStage7ConnectionState(connection,hasRows);
  if(!state)return null;
  return <StageXState compact kind={state.kind as SmmStageXKind} title={state.title} detail={state.detail}/>;
}

function Stage7Empty({segment}:{segment:SmmStage7Segment}){
  return <StageXState
    kind="EMPTY"
    title={segment==='ACTIVE'?'暫時沒有進行中訂單':'暫時沒有歷史訂單'}
    detail={segment==='ACTIVE'?'有新正式訂單時會按目前狀態顯示。':'以前營業日及已完成、已取消訂單會保留喺歷史記錄。'}
  />;
}

function Stage7Card({row,onOpen}:{row:SmmOrderProjection;onOpen:()=>void}){
  const count=smmStage7ItemCount(row),status=smmStage7StatusLabel(row);
  return <button type="button" className="stage7-card" onClick={onOpen}>
    <span className="v3-order-card-top"><strong>{row.displayCode||'未有資料'}</strong><small>{sourceLabel(row)}</small><span className={`stage7-status stage7-status-${statusTone(status)}`}>{status}</span></span>
    <span className="v3-order-card-items">{row.itemSummary||'未有商品明細'}</span>
    <span className="v3-order-card-bottom"><small>{row.tenderLabel||timeLabel(smmStage7OrderTime(row))}{count===null?'':`・${count} 件`}</small><strong>{smmStage7AmountLabel(row)}</strong></span>
  </button>;
}

function Stage7Search({
  rows,
  query,
  setQuery,
  scope,
  setScope,
  onBack,
  onOpen,
  incomplete,
  server,
}:{
  rows:readonly SmmOrderProjection[];
  query:string;
  setQuery:(value:string)=>void;
  scope:SmmStage7SearchScope;
  setScope:(value:SmmStage7SearchScope)=>void;
  onBack:()=>void;
  onOpen:(row:SmmOrderProjection)=>void;
  incomplete:boolean;
  server?:ServerSearch;
}){
  const phoneAvailable=!server&&rows.some(row=>Boolean(smmStage7Phone(row)));
  const effectiveScope=scope==='PHONE'&&!phoneAvailable?'ALL':scope;
  const localResults=useMemo(()=>rows.filter(row=>smmStage7MatchesSearch(row,query,effectiveScope)),[rows,query,effectiveScope]);
  const results=server?server.result?.rows??[]:localResults;
  return <section className="stage7-search-screen" data-stage7-visual="7.3_SEARCH">
    <header className="stage7-subheader">
      <button type="button" onClick={onBack} aria-label="返回訂單列表">‹</button>
      <strong>搜尋訂單</strong>
      <span/>
    </header>
    <form className="stage7-search-form" onSubmit={event=>{event.preventDefault();server?.onSubmit()}}>
    <label className="stage7-search-box">
      <span aria-hidden="true">⌕</span>
      <input autoFocus maxLength={server?80:undefined} value={query} onChange={event=>setQuery(event.target.value)} placeholder={server?'搜尋已同步的訂單編號 / 商品':phoneAvailable?'搜尋已載入的訂單編號 / 商品 / 電話':'搜尋已載入的訂單編號 / 商品'}/>
      {query?<button type="button" onClick={()=>setQuery('')} aria-label="清除搜尋">×</button>:null}
    </label>
    {server?<button className="stage7-refresh-button" type="submit" disabled={server.busy||server.disabled}>{server.busy?'搜尋中…':'搜尋'}</button>:null}
    </form>
    <div className="stage7-search-scopes">
      {SEARCH_SCOPES.filter(([value])=>value!=='PHONE'||phoneAvailable).map(([value,label])=><button
        type="button"
        key={value}
        className={effectiveScope===value?'active':''}
        aria-pressed={effectiveScope===value}
        onClick={()=>setScope(value)}
      >{label}</button>)}
    </div>
    {server?.error?<p className="stage7-summary" role="alert">{server.error}</p>:null}
    {server?<>
      <p className="stage7-summary">{server.result?`「${server.result.page.query.search}」・${server.result.page.query.view==='HISTORY'?'歷史':'進行中'}${server.result.page.query.from?`・${server.result.page.query.from} 至 ${server.result.page.query.to}`:''}・已讀回 ${results.length} 張`:'搜尋目前範圍的已同步訂單；輸入後按搜尋。'} 來源完整度未確認。</p>
      {server.result?(results.length?<div className="stage7-list">{results.map(row=><Stage7Card key={row.orderId} row={row} onOpen={()=>onOpen(row)}/>)}</div>:<section className="stage7-search-hint"><strong>未有符合結果</strong><span>請核對搜尋內容或切換進行中／歷史範圍。</span></section>):null}
      {server.result?.page.hasMore?<button type="button" className="stage7-refresh-button" disabled={server.busy||server.disabled} onClick={server.onMore}>{server.busy?'載入中…':'載入更多搜尋結果'}</button>:null}
    </>:!query.trim()?<section className="stage7-search-hint"><strong>搜尋目前已載入的訂單</strong><span>{phoneAvailable?'可比對訂單編號、商品名稱及獲准讀取的電話。':'可比對訂單編號及商品名稱；目前未提供可搜尋的電話。'}</span></section>:
      results.length?<div className="stage7-list">{results.map(row=><Stage7Card key={row.orderId} row={row} onOpen={()=>onOpen(row)}/>)}</div>:
      <section className="stage7-search-hint"><strong>已載入的訂單未有符合結果</strong><span>{incomplete?'其他未讀回訂單仍未能搜尋，請在收銀機核對。':'請核對搜尋內容或切換進行中／歷史範圍。'}</span></section>}
  </section>;
}

function DetailField({label,value}:{label:string;value:string|undefined|null}){
  const text=String(value??'').trim();
  return <div className="stage7-field"><span>{label}</span><strong>{text||'未有資料'}</strong></div>;
}

function Stage7Detail({
  row,
  surface,
  refreshing,
  onBack,
  onStatus,
  onRefresh,
  onDrinks,
  onPayment,
  onCancel,
  message,
}:{
  row:SmmOrderProjection;
  surface:'DETAIL'|'STATUS';
  refreshing:boolean;
  onBack:()=>void;
  onStatus:()=>void;
  onRefresh:()=>void;
  onDrinks?:()=>void;
  onPayment?:()=>void;
  onCancel?:()=>void;
  message?:string;
}){
  const count=smmStage7ItemCount(row);
  const status=smmStage7StatusLabel(row);
  const phone=smmStage7Phone(row);
  const structured=row.items??[];
  const canCancel=Boolean(onCancel&&row.source.trim().toUpperCase()==='KEETA'&&['待確認','製作中','準備完成 / 可取餐'].includes(status));
  const operationsNote=row.source.trim().toUpperCase()==='KEETA'?(canCancel?'取消可在訂單詳情處理；平台退款按 Keeta 安排。':'平台退款按 Keeta 安排。'):'取消或退款請在收銀機處理。';

  if(surface==='STATUS'){
    return <section className="stage7-status-screen" data-stage7-visual="7.5_STATUS">
      <header className="stage7-subheader">
        <button type="button" onClick={onBack} aria-label="返回訂單詳情">‹</button>
        <strong>訂單狀態</strong>
        <span/>
      </header>
      <section className="stage7-status-order">
        <div><strong>{row.displayCode||'未有資料'}</strong><span>{sourceLabel(row)}</span></div>
        <small>{count===null?'項目數未有資料':`${count} 項`} · {smmStage7AmountLabel(row)}</small>
      </section>
      <section className="stage7-detail-section"><strong className={`stage7-status stage7-status-${statusTone(status)}`}>{status}</strong><p>{operationsNote}</p>{message?<p role="alert">{message}</p>:null}</section>
      <footer className="stage7-status-footer"><button type="button" onClick={onBack}>返回訂單</button><button type="button" className="primary" disabled={refreshing} onClick={onRefresh}>{refreshing?'重新讀取中…':'重新整理狀態'}</button></footer>
    </section>;
  }

  return <section className="stage7-detail" data-stage7-visual="7.4_DETAIL">
    <header className="stage7-subheader">
      <button type="button" onClick={onBack} aria-label="返回訂單列表">‹</button>
      <strong>訂單詳情</strong>
      <span>•••</span>
    </header>

    <section className="stage7-detail-hero">
      <div><strong>{row.displayCode||'未有資料'}</strong><span>{sourceLabel(row)}</span></div>
      <span className={`stage7-status stage7-status-${statusTone(status)}`}>{status}</span>
      <small>下單時間　{dateTimeLabel(smmStage7OrderTime(row))}</small>
      {row.eta?<small>預計取餐　{row.eta}</small>:<small>預計取餐　未有資料</small>}
    </section>

    <section className="stage7-detail-section">
      <h3>顧客資料</h3>
      <DetailField label="姓名" value={row.customerName}/>
      <DetailField label="電話" value={phone}/>
    </section>

    <ol className="ui-order-progress" aria-label="訂單進度">{['待確認','製作中','準備完成 / 可取餐','已取餐'].map(label=><li key={label} className={status===label?'current':''} aria-current={status===label?'step':undefined}>{label==='準備完成 / 可取餐'?'可取餐':label}</li>)}</ol>

    <section className="stage7-detail-section">
      <h3>訂單內容{count===null?'':`（${count} 項）`}</h3>
      {structured.length?<div className="stage7-items">{structured.map((item,index)=><article key={index}>
        <b>{item.quantity}</b>
        <div><strong>{item.name||'未有資料'}</strong><small>{item.detail||item.remark||'未有資料'}</small></div>
        <span>{item.amountLabel||'未有資料'}</span>
      </article>)}</div>:
      <p className="stage7-summary">{row.itemSummary||'未有資料'}</p>}
    </section>

    <section className="stage7-detail-section stage7-money">
      <h3>金額 / 付款</h3>
      <DetailField label="有效總額" value={smmStage7AmountLabel(row)}/>
      <DetailField label="付款方式" value={row.tenderLabel}/>
    </section>

    <section className="stage7-detail-section">
      <h3>來源 / 出餐進度</h3>
      <DetailField label="來源" value={sourceLabel(row)}/>
      <DetailField label="外部參考" value={row.externalRef}/>
      <DetailField label="目前狀態" value={status}/>
    </section>

    <section className="stage7-detail-section">
      <h3>訂單紀錄</h3>
      {row.timeline.length?<div className="stage7-timeline">{row.timeline.map((item,index)=><article key={index}>
        <span/>
        <div><strong>{item.label||'未有資料'}</strong><small>{item.detail||'未有資料'}</small></div>
        <time>{dateTimeLabel(item.at)}</time>
      </article>)}</div>:<p className="stage7-summary">未有資料</p>}
    </section>

    {row.note?<section className="stage7-note"><strong>訂單備註</strong><span>{row.note}</span></section>:null}

    {message?<p role="alert" className="stage7-summary">{message}</p>:null}
    <footer className="stage7-detail-footer">
      {canCancel?<button type="button" onClick={onCancel}>取消 Keeta 訂單</button>:null}
      {onPayment&&smmStage7SourceGroup(row)==='OWN_PLATFORM'&&status==='待確認'?<button type="button" onClick={onPayment}>核對付款</button>:null}
      {onDrinks&&status!=='已取消'?<button type="button" className="stage7-drink-button" onClick={onDrinks}>補飲品</button>:null}
      <button type="button" onClick={onStatus}>查看狀態</button>
      <button type="button" className="primary" disabled={refreshing} onClick={onRefresh}>{refreshing?'重新讀取中…':'重新整理'}</button>
      <small>{operationsNote}</small>
    </footer>
  </section>;
}

export function Stage7OrdersView({
  connection,
  rows,
  onRefresh,
  initialTab,
  incomplete=false,
  paging,
  pagingBusy=false,
  onPage,
  onSearchPage,
  onDrinks,
  onPayment,
  onCancel,
}:{
  connection:SmmConnectionState;
  rows:readonly SmmOrderProjection[];
  onRefresh:()=>Promise<void>|void;
  initialTab?:'PENDING'|'WORKING'|'READY'|'HISTORY';
  incomplete?:boolean;
  paging?:SmmReadModelSnapshot['orderPages'];
  pagingBusy?:boolean;
  onPage?:(query:MobileOrderQuery)=>Promise<void>;
  onSearchPage?:(query:MobileOrderQuery)=>Promise<MobileOrderPage>;
  onDrinks?:(orderId:string)=>void;
  onPayment?:(orderId:string,onReadback:()=>void)=>void;
  onCancel?:(orderId:string,displayCode:string,onReadback:()=>void)=>void;
}){
  const [segment,setSegment]=useState<SmmStage7Segment>('ACTIVE');
  const [sourceFilter,setSourceFilter]=useState<SmmStage7SourceFilter>('ALL');
  const [serviceFilter,setServiceFilter]=useState<ServiceFilter>('ALL');
  const [dateFilter,setDateFilter]=useState<SmmStage7DateFilter>('ALL');
  const [statusFilter,setStatusFilter]=useState<SmmStage7StatusFilter>('ALL');
  const [customDate,setCustomDate]=useState('');
  const [query,setQuery]=useState('');
  const [searchScope,setSearchScope]=useState<SmmStage7SearchScope>('ALL');
  const [surface,setSurface]=useState<Stage7Surface>('LIST');
  const [selectedId,setSelectedId]=useState<string|null>(null);
  const [refreshing,setRefreshing]=useState(false);
  const [searchResult,setSearchResult]=useState<SearchResult|null>(null);
  const [searchBusy,setSearchBusy]=useState(false);
  const [searchError,setSearchError]=useState('');
  const [selectedResult,setSelectedResult]=useState<SmmOrderProjection|null>(null);
  const [detailReturn,setDetailReturn]=useState<'LIST'|'SEARCH'>('LIST');
  const searchRequest=useRef(0),serverSearch=Boolean(paging&&onSearchPage);
  useEffect(()=>{
    searchRequest.current++;setSearchBusy(false);setSearchResult(null);setSearchError('');setSurface('LIST');setSelectedId(null);setSelectedResult(null);
    setSegment(initialTab==='HISTORY'?'HISTORY':'ACTIVE');
    setStatusFilter(initialTab==='WORKING'?'WORKING':initialTab==='READY'?'READY':'ALL');
  },[initialTab]);

  const segmentRows=useMemo(()=>smmStage7Sort(rows.filter(row=>smmStage7InSegment(row,segment))),[rows,segment]);
  const filteredRows=useMemo(()=>segmentRows.filter(row=>
    matchesService(row,serviceFilter)&&smmStage7MatchesSource(row,sourceFilter)&&
    smmStage7MatchesStatus(row,statusFilter)&&
    (segment!=='HISTORY'||paging||smmStage7MatchesDate(row,dateFilter,customDate))
  ),[segmentRows,serviceFilter,sourceFilter,statusFilter,segment,dateFilter,customDate,paging]);
  const selected=selectedId?(detailReturn==='SEARCH'&&selectedResult?.orderId===selectedId?selectedResult:rows.find(row=>row.orderId===selectedId)):undefined;
  const leaveSearch=()=>{searchRequest.current++;setSearchBusy(false);setSurface('LIST')};
  const changeQuery=(value:string)=>{setQuery(value);if(serverSearch&&!value){leaveSearch();setSearchResult(null);setSearchError('')}};
  const search=async(more=false)=>{
    if(!onSearchPage||searchBusy||pagingBusy)return;
    if(!query.trim()&&!more){changeQuery('');return;}
    const base=segment==='ACTIVE'?paging?.active.query:paging?.history.query;
    const input:MobileOrderQuery=more&&searchResult?{...searchResult.page.query,cursor:searchResult.page.nextCursor!}:{view:segment,...(base?.from?{from:base.from,to:base.to}:{}),search:query,searchScope:searchScope==='PHONE'?'ALL':searchScope};
    const request=++searchRequest.current;setSearchBusy(true);setSearchError('');
    try{
      const result=operationalOrderSearchPage(await onSearchPage(input),input);
      if(request!==searchRequest.current)return;
      if(more&&searchResult&&result.page.readFence!==searchResult.page.readFence)throw Error('MOBILE_ORDER_CURSOR_STALE');
      setSearchResult(more&&searchResult?{...result,rows:[...new Map([...searchResult.rows,...result.rows].map(row=>[row.orderId,row])).values()]}:result);
    }catch(error){if(request===searchRequest.current)setSearchError((error as {status?:number})?.status===401?'登入需要重新確認，搜尋結果及購物草稿已保留。':'未能讀取搜尋結果，請再按搜尋。已讀回結果保留。');}
    finally{if(request===searchRequest.current)setSearchBusy(false);}
  };

  const refresh=async()=>{
    if(refreshing)return;
    setRefreshing(true);
    try{
      if(detailReturn==='SEARCH'&&selected&&onSearchPage){
        const input:MobileOrderQuery={view:selected.history?'HISTORY':'ACTIVE',search:selected.orderId,searchScope:'DISPLAY'};
        const result=operationalOrderSearchPage(await onSearchPage(input),input),updated=result.rows.find(row=>row.orderId===selected.orderId);
        if(updated){setSelectedResult(updated);setSearchError('');setSearchResult(previous=>previous?{...previous,rows:previous.rows.map(row=>row.orderId===updated.orderId?updated:row)}:previous);}
        else setSearchError('暫未讀回此訂單，保留上次資料。');
      }else await Promise.resolve(onRefresh());
    }catch{setSearchError('未能重新讀取，保留上次資料。');}
    finally{setRefreshing(false)}
  };

  const openDetail=(row:SmmOrderProjection)=>{setSelectedId(row.orderId);setSelectedResult(row);setDetailReturn(surface==='SEARCH'?'SEARCH':'LIST');setSurface('DETAIL')};
  const backToList=()=>{setSelectedId(null);setSelectedResult(null);setSurface(detailReturn)};

  if(selected&&surface==='DETAIL'){
    return <Stage7Detail row={selected} surface="DETAIL" refreshing={refreshing} onBack={backToList} onStatus={()=>setSurface('STATUS')} onRefresh={()=>void refresh()} onDrinks={onDrinks?()=>onDrinks(selected.orderId):undefined} onPayment={onPayment?()=>onPayment(selected.orderId,()=>void refresh()):undefined} onCancel={onCancel?()=>onCancel(selected.orderId,selected.displayCode,()=>void refresh()):undefined} message={searchError}/>;
  }
  if(selected&&surface==='STATUS'){
    return <Stage7Detail row={selected} surface="STATUS" refreshing={refreshing} onBack={()=>setSurface('DETAIL')} onStatus={()=>{}} onRefresh={()=>void refresh()}/>;
  }
  if(surface==='SEARCH'){
    return <><Stage7Search
      rows={segmentRows}
      query={query}
      setQuery={changeQuery}
      scope={searchScope}
      setScope={setSearchScope}
      onBack={leaveSearch}
      onOpen={openDetail}
      incomplete={incomplete}
      server={serverSearch?{result:searchResult,busy:searchBusy,disabled:pagingBusy,error:searchError,onSubmit:()=>void search(),onMore:()=>void search(true)}:undefined}
    />{!serverSearch?<Stage7Paging page={segment==='ACTIVE'?paging?.active:paging?.history} busy={pagingBusy} onPage={onPage}/>:null}</>;
  }

  return <section className="stage7-page" aria-label="訂單記錄" data-stage7-visual={segment==='ACTIVE'?'7.1_ACTIVE':'7.2_HISTORY'}>
    {!initialTab?<header className="stage7-header">
      <h1>訂單</h1>
      <button type="button" onClick={()=>setSurface('SEARCH')} aria-label="搜尋訂單">⌕</button>
    </header>:null}

    <Stage7StateBanner connection={connection} hasRows={rows.length>0}/>

    {!initialTab?<div className="stage7-segments" aria-label="訂單範圍">
      <button type="button" className={segment==='ACTIVE'?'active':''} aria-pressed={segment==='ACTIVE'} onClick={()=>{setSegment('ACTIVE');setDateFilter('ALL');setStatusFilter('ALL')}}>進行中</button>
      <button type="button" className={segment==='HISTORY'?'active':''} aria-pressed={segment==='HISTORY'} onClick={()=>{setSegment('HISTORY');setStatusFilter('ALL')}}>歷史</button>
    </div>:null}

    {segment==='HISTORY'&&paging?<div className="stage7-date-filter" aria-label="歷史營業日">
      <button type="button" disabled={pagingBusy} onClick={()=>void onPage?.({view:'HISTORY'})}>全部歷史</button>
      <input type="date" value={customDate} onChange={event=>setCustomDate(event.target.value)} aria-label="選擇歷史營業日"/>
      <button type="button" disabled={pagingBusy||!customDate} onClick={()=>void onPage?.({view:'HISTORY',from:customDate,to:customDate})}>載入這個營業日</button>
    </div>:segment==='HISTORY'?<div className="stage7-date-filter" aria-label="歷史日期">
      {([
        ['ALL','全部'],['TODAY','今天'],['YESTERDAY','昨天'],['CUSTOM','自訂日期'],
      ] as const).map(([value,label])=><button type="button" key={value} className={dateFilter===value?'active':''} onClick={()=>setDateFilter(value)}>{label}</button>)}
      {dateFilter==='CUSTOM'?<input type="date" value={customDate} onChange={event=>setCustomDate(event.target.value)} aria-label="選擇歷史日期"/>:null}
    </div>:null}

    <div className="v3-order-services" aria-label="訂單服務方式">
      {([['ALL','全部'],['TAKEAWAY','外賣'],['DINE_IN','堂食'],['ATTENTION','要處理']] as const).map(([value,label])=><button type="button" key={value} aria-pressed={serviceFilter===value} onClick={()=>setServiceFilter(value)}>{label}<b>{segmentRows.filter(row=>matchesService(row,value)).length}</b></button>)}
      <button type="button" className="v3-order-search" onClick={()=>setSurface('SEARCH')} aria-label="搜尋訂單">⌕</button>
    </div>
    <details className="v3-order-filter-details"><summary>來源／篩選</summary>
    <div className="stage7-source-filters" aria-label="已載入訂單來源">
      {SOURCE_FILTERS.map(([value,label])=><button type="button" key={value} className={sourceFilter===value?'active':''} aria-pressed={sourceFilter===value} onClick={()=>setSourceFilter(value)}>
        <span>{label}</span><b>{sourceFilterCount(segmentRows,value)}</b>
      </button>)}
    </div>

    {!initialTab?<div className="stage7-status-filters" aria-label="訂單狀態">
      {(segment==='ACTIVE'?ACTIVE_STATUS_FILTERS:HISTORY_STATUS_FILTERS).map(([value,label])=><button type="button" key={value} className={statusFilter===value?'active':''} aria-pressed={statusFilter===value} onClick={()=>setStatusFilter(value)}>{label}</button>)}
    </div>:null}

    <button type="button" className="stage7-search-entry" onClick={()=>setSurface('SEARCH')}>
      <span aria-hidden="true">⌕</span><span>{query||(serverSearch?'搜尋已同步訂單':'搜尋已載入的訂單')}</span>
    </button>

    </details>

    {connection==='READY'&&!incomplete&&segmentRows.length===0?<Stage7Empty segment={segment}/>:null}

    {segmentRows.length>0&&filteredRows.length===0?<section className="stage7-filter-empty" data-stage7-filter-empty="true" role="status">
      <strong>目前篩選條件沒有符合訂單</strong>
      <span>目前範圍已載入 {segmentRows.length} 張訂單；請調整來源、狀態或日期篩選。{incomplete?'其他未讀回訂單請在收銀機核對。':''}</span>
    </section>:null}

    {filteredRows.length?<div className="stage7-list">{filteredRows.map(row=><Stage7Card key={row.orderId} row={row} onOpen={()=>openDetail(row)}/>)}</div>:null}

    <Stage7Paging page={segment==='ACTIVE'?paging?.active:paging?.history} busy={pagingBusy} onPage={onPage}/>

    <button type="button" className="stage7-refresh-button" disabled={refreshing} onClick={()=>void refresh()}>{refreshing?'重新整理中…':'重新整理訂單'}</button>
  </section>;
}
